import { Fragment } from 'react';
import { EmptyState, Skeleton } from '../../../components';
import { useLang } from '../../../i18n';
import type { CompanyValue } from '../../../types';
import { resolveIcon } from '../hooks/useIcon';
import './ValuesSection.scss';

export interface ValuesSectionProps {
  values: CompanyValue[];
  loading: boolean;
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export default function ValuesSection({ values, loading }: ValuesSectionProps) {
  const { t } = useLang();
  const showSkeleton = loading && values.length === 0;
  const empty = !loading && values.length === 0;
  const rows = chunk(values, 3);

  return (
    <section className="section section--tight about-values">
      <div className="container">
        <div className="section-header about-values__head">
          <span className="eyebrow">{t('about.values.t')}</span>
          <h2 className="about-values__title">{t('about.values.sub')}</h2>
        </div>

        {empty ? (
          <EmptyState title={t('about.empty')} />
        ) : showSkeleton ? (
          <div className="about-values__row">
            {[0, 1, 2].map((i) => (
              <div key={i} className="about-values__item">
                <Skeleton width={48} height={20} />
                <Skeleton width={48} height={48} shape="rect" />
                <Skeleton width="70%" height={20} />
                <Skeleton width="100%" height={14} />
              </div>
            ))}
          </div>
        ) : (
          <div className="about-values__list">
            {rows.map((row, ri) => (
              <Fragment key={ri}>
                <div className="about-values__row">
                  {row.map((v, ci) => {
                    const Icon = resolveIcon(v.icon_key);
                    const idx = ri * 3 + ci + 1;
                    return (
                      <article key={v.id} className="about-values__item">
                        <span className="tech-label about-values__number">{pad(idx)}</span>
                        <span className="about-values__icon" aria-hidden="true">
                          <Icon size={22} />
                        </span>
                        <h3 className="about-values__h3">{v.title}</h3>
                        <p className="about-values__text">{v.description}</p>
                      </article>
                    );
                  })}
                </div>
              </Fragment>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
